import { sortNamesBy } from 'logic/sortNames';
import { BuildingId, DeviceConfig, DeviceConfigId, DeviceTypeId } from 'shared/models';
import { ChangeEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceConfigDialog } from 'ui/devices/deviceConfigDialog/DeviceConfigDialog';
import { SelectField } from 'ui/shared/controls';
import { getErrorByProtocol } from 'data/protocols';
import { useProtocols } from 'hooks';

require('./DeviceConfigField.css');

export type Props = {
    id: string;
    className?: string;
    disabled?: boolean;
    readOnly?: boolean;
    name: string;
    value: DeviceConfigId | null;
    deviceConfigs: DeviceConfig[];
    onChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;

    // These three fields are needed because a deeper component does a theme preview and needs to know what themes to display
    buildingId: BuildingId;
    deviceTypeId: DeviceTypeId | null;
    isPI: boolean;
};

const nullValue = 'none';

export const DeviceConfigField = ({ id, className = '', disabled = false, readOnly = false, name, value, deviceConfigs, onChange, buildingId, deviceTypeId, isPI }: Props) => {
    const { t } = useTranslation();
    const [ dialogVisible, setDialogVisible ] = useState(false);
    const { protocols } = useProtocols({ buildingId });

    // Take the new ID from the configuration dialog and fake a
    // change event with the field name and new desired value.
    // This will happen if a config is deleted (will be null)
    // or is saved as a new config (will be the new ID)
    const handleChangeId = useCallback((deviceConfigId: DeviceConfigId | null) => {
        onChange({
            currentTarget: {
                name,
                type: 'text',
                value: deviceConfigId || nullValue,
            },
        } as any);
    }, [ onChange, name ]);

    const deviceConfigOptions = useMemo(() => {
        const options = deviceConfigs.sort(sortNamesBy(dc => dc.displayName)).map(dc => {
            const protocol = protocols && protocols[dc.protocolId];
            return {
                label: dc.displayName,
                value: dc.id,
                isDisabled: !!getErrorByProtocol(protocol),
            };
        });
        return [ { label: t('NONE'), value: null, isPlaceholder: true }, ...options ];
    }, [ deviceConfigs, protocols, t ]);

    const renderDeviceConfigOption = useCallback((deviceConfigId: DeviceConfigId | null): ReactNode => {
        if (!deviceConfigId || !deviceConfigs.length) {
            return (
                <div>
                    { t('NONE') }
                </div>
            );
        }
        const deviceConfig = deviceConfigs.find(dc => dc.id === deviceConfigId)!;
        const protocol = deviceConfig && protocols && protocols[deviceConfig.protocolId];
        const error = getErrorByProtocol(protocol, t);
        return (
            <div data-id={ deviceConfigId } title={ error || undefined }>
                { (!deviceConfig || error) &&
                    <i className="yellow exclamation triangle icon"/>
                }
                { deviceConfig ? deviceConfig.displayName : t(`ERROR.MISSING_DEVICE_CONFIG`) }
            </div>
        );
    }, [ protocols, deviceConfigs, t ]);

    return (
        <div
            id={ id }
            className={ `device-config-field ${className}` }
        >
            <SelectField
                id={ `${id}-select` }
                required={ true }
                disabled={ disabled }
                readOnly={ readOnly }
                placeholderKey='NONE'
                labelKey="LABEL.CONFIGURATION"
                name={ name }
                value={ value || nullValue }
                options={ deviceConfigOptions }
                renderOption={ renderDeviceConfigOption }
                onChange={ handleChangeId }
            />
            
            <button
                type="button"
                className="ui icon button"
                onClick={ () => setDialogVisible(true) }
                disabled={ disabled }
            >
                <i className={ `ui large grey ${value ? 'cog' : 'plus'} icon` }/>
            </button>

            <DeviceConfigDialog
                visible={ dialogVisible }
                buildingId={ buildingId }
                deviceTypeId={ deviceTypeId }
                readOnly={ readOnly }
                isPI={ isPI }
                deviceConfigId={ value }
                onClose={ () => setDialogVisible(false) }
                onChangeId={ handleChangeId }
            />
        </div>
    );
};
